/**
 * Live sanitization for currency price inputs (list / strike-off).
 * Matches the historical Add Package modal typing rules.
 */

export interface SanitizePriceInputOptions {
  /** Reject literal 0 / 0.0 / 0.00 (used by strike-off fields). */
  blockZero?: boolean;
  /** Cap numeric value (default 999999.99). */
  max?: number;
}

/**
 * Returns the sanitized display string, or `null` when the input should be cleared.
 */
export function sanitizePriceInputValue(
  raw: unknown,
  options: SanitizePriceInputOptions = {}
): string | null {
  const blockZero = options.blockZero === true;
  const max = options.max ?? 999999.99;

  if (raw === '' || raw === null || raw === undefined) {
    return null;
  }

  let value = raw.toString().replace(/[^0-9.]/g, '');

  if (blockZero && (value === '0' || value === '0.0' || value === '0.00')) {
    return null;
  }

  const parts = value.split('.');
  if (parts.length > 2) {
    value = parts[0] + '.' + parts.slice(1).join('');
  }
  if (parts.length === 2) {
    parts[1] = parts[1].slice(0, 2);
    value = parts[0] + '.' + parts[1];
  }

  if (value.indexOf('.') === -1) {
    value = value.replace(/^0+/, '');
  } else {
    const beforeDecimal = value.split('.')[0];
    const afterDecimal = value.split('.')[1] || '';
    const cleanedBefore = beforeDecimal.replace(/^0+/, '') || '0';
    value = cleanedBefore + '.' + afterDecimal;
  }

  if (value === '' || value === '.') {
    return null;
  }

  const numericValue = parseFloat(value);
  if (!isNaN(numericValue) && numericValue > max) {
    value = max.toFixed(2);
  }

  return value;
}
